import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FUEL_TYPE_LABELS, type FuelType } from '@shared/domain';
import { api } from '../../api';
import { Badge, Dialog, ErrorBox, Field, Loading, RupeeInput, dt, inr, rupeesToPaise, useBusy } from '../../ui';

interface PriceRow { locationId: string; locationName: string; fuelType: string; price: { unitPricePaise: string; effectiveFrom: string } | null }

/** Today's price per fuel and pump. Shown on the pump home so it can be changed first thing each day. */
export function Prices() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['prices'], queryFn: () => api.get<{ current: PriceRow[] }>('/api/prices') });
  const [edit, setEdit] = useState<PriceRow | null>(null);
  const [val, setVal] = useState('');
  const { busy, error, run } = useBusy();
  return (
    <div className="card">
      <h2>Today’s fuel prices</h2>
      <p className="muted">Used to estimate litre indents and to check the pump display. A new price applies from now; past supplies keep their price.</p>
      {q.isLoading ? <Loading /> : null}
      <table>
        <tbody>
          {q.data?.current.map((p) => (
            <tr key={`${p.locationId}${p.fuelType}`}>
              <td>{p.locationName}</td>
              <td>{FUEL_TYPE_LABELS[p.fuelType as FuelType]}</td>
              <td className="num">{p.price ? `${inr(p.price.unitPricePaise)}/L` : <Badge tone="bad">not set</Badge>}<div><small>{p.price ? `from ${dt(p.price.effectiveFrom)}` : ''}</small></div></td>
              <td><button className="btn small" onClick={() => { setEdit(p); setVal(''); }}>Change</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <Dialog open={Boolean(edit)} title={`New ${edit ? FUEL_TYPE_LABELS[edit.fuelType as FuelType] : ''} price`} onClose={() => setEdit(null)}>
        <Field label="Price per litre (₹)"><RupeeInput value={val} onChange={setVal} placeholder="102.86" autoFocus /></Field>
        <ErrorBox error={error} />
        <button className="btn primary" disabled={busy || !rupeesToPaise(val)} onClick={() => void run(async () => { await api.post('/api/prices', { locationId: edit!.locationId, category: 'FUEL', fuelType: edit!.fuelType, unitPricePaise: rupeesToPaise(val) }); setEdit(null); await qc.invalidateQueries({ queryKey: ['prices'] }); })}>Save price</button>
      </Dialog>
    </div>
  );
}
